import React, { useState, useEffect } from "react";
import { colors } from "../constants/colors";
import styled, { css } from "styled-components";
import produce from "immer";
import { useImages } from "../hooks/useImages";
import { ReactComponent as Check } from "../assets/svg/check.svg";
import { ReactComponent as Wrong } from "../assets/svg/wrong.svg";

const LessonDataUnion = ({ seed, freeze = false, onChange = () => {} }) => {
  const [state, setState] = useState([]);
  const [palette, setPalette] = useState([]);
  const [union, setUnion] = useState({ bind: undefined });

  const [extractedImages, setExtractedImages] = useState([]);
  const images = useImages(extractedImages);

  useEffect(() => {
    const state = initState(seed);
    setState(state);
    setPalette(initColors(seed.map((i) => i.color)));
    setUnion({ bind: undefined });
    setExtractedImages(state.map((i) => i.image));
  }, [seed]);

  useEffect(() => {
    onChange({
      ready: isReady(state),
      data: state,
    });
  }, [state, onChange]);

  const isLoaded = ({ image }) => (image && images[image]) || !image;

  const getUnselectedColor = () => palette.find((i) => !i.selected)?.color;

  const selectColor = (color) => {
    if (!color) return;
    setPalette(
      produce((draftState) => {
        const index = draftState.findIndex((i) => i.color === color);
        if (index >= 0) draftState[index].selected = true;
      })
    );
  };

  const unselectColor = (color) => {
    if (!color) return;
    setPalette(
      produce((draftState) => {
        const index = draftState.findIndex((i) => i.color === color);
        if (index >= 0) draftState[index].selected = false;
      })
    );
  };

  const reset = (index) => {
    setState(
      produce((draftState) => {
        if (!draftState[index]) return;
        unselectColor(draftState[index].color);

        const pair = draftState[index].pair;

        draftState[index].pair = null;
        draftState[index].color = null;

        if (pair === null) return;

        draftState[pair].pair = null;
        draftState[pair].color = null;
      })
    );
  };

  const initUnion = (index1) => {
    reset(index1);

    const color = getUnselectedColor();

    setState(
      produce((draftState) => {
        draftState[index1].color = color;
      })
    );

    return (index2) => {
      if (index1 === index2) {
        reset(index1);
        return;
      }

      reset(index2);

      setState(
        produce((draftState) => {
          draftState[index1].color = color;
          draftState[index1].pair = index2;

          draftState[index2].color = color;
          draftState[index2].pair = index1;
        })
      );

      selectColor(color);
    };
  };

  const onClick = (index) => {
    if (!union.bind) setUnion({ bind: initUnion(index) });
    else {
      union.bind(index);
      setUnion({ bind: undefined });
    }
  };

  return (
    <Container freeze={freeze}>
      {state.map((item, index) => (
        <BoxWrapper key={index}>
          {item.state && <StateIcon as={stateIcon[item.state]} />}
          <Box color={item.color} onClick={() => onClick(index)}>
            {isLoaded(item) ? (
              <>
                {item.image && (
                  <ImageWrapper>
                    <Image src={images[item.image]} />
                  </ImageWrapper>
                )}
                {item.text && <Label>{item.text}</Label>}
              </>
            ) : (
              <Spinner src="loading.gif" />
            )}
          </Box>
        </BoxWrapper>
      ))}
    </Container>
  );
};

const stateIcon = {
  SUCCESS: Check,
  FAILURE: Wrong,
};

const initState = (seed) => {
  let state = formatState(seed);
  return state;
};

const formatState = (state) =>
  state.map((item) => ({
    ...item,
    color: item.pair === null ? null : item.color,
  }));

const initColors = (arraySelected = []) =>
  produce(colors, (draftState) => {
    draftState.forEach((color, index, array) => {
      array[index] = {
        color: color,
        selected: arraySelected.findIndex((i) => i === color) !== -1,
      };
    });
  });

const isReady = (data) => {
  let ready = true;
  for (let item of data)
    if (item.pair === null) {
      ready = false;
      break;
    }
  return ready;
};


const Container = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.freeze &&
    css`
      pointer-events: none;
    `}
`;

const BoxWrapper = styled.div`
  position: relative;
  margin: 7px;
  max-width: 150px;
  width: 100%;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 80px;
  border-radius: 20px;
  overflow: hidden;
  background-color: white;
  user-select: none;
  cursor: pointer;
  ${(props) =>
    props.color &&
    css`
      border: 4px solid ${props.color};
      //box-shadow: 0px 0px 0px 4px ${props.color};
    `}
`;

const StateIcon = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  position: absolute;
  right: 0px;
  top: 0px;
  transform: translate(35%, -35%);
`;

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 200px;
  min-height: 100px;
  background-color: black;
`;

const Image = styled.img`
  width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const Label = styled.div`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  font-size: 0.9em;
  text-align: center;
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

export default LessonDataUnion;
